import type { StrokePoint, StrokeRun } from "./stroke-shape.js";
import { placeSteps, shapeSteps } from "./stroke-shape.js";

/**
 * One straight run of a stroke, put on the grid.
 *
 * The two ends arrive already scaled into the unit square, and what comes back
 * is three whole numbers. `stroke-shape.ts` is what the square and the runs are
 * for.
 */

/**
 * A fraction of the way from nothing to one, as a whole number of steps.
 *
 * For a figure with two ends, which is what a length and a position are. Nought
 * maps to nought and one to the last step, so both ends are reachable.
 */
function step(fraction: number, steps: number): number {
  return Math.max(0, Math.min(steps - 1, Math.round(fraction * (steps - 1))));
}

/**
 * A fraction of the way round the circle, as a whole number of steps.
 *
 * For a figure with no ends. A whole turn is the same direction as none, so it
 * wraps to nought rather than reaching a last step of its own. Written as
 * `step` above it, due west came out 63 and due south 47, which put the four
 * cardinal directions half a step off the grid they were supposed to be on and
 * left one of the 64 unreachable.
 */
function turnStep(fraction: number, steps: number): number {
  return Math.round(fraction * steps) % steps;
}

/** One run, between two corners already scaled into the unit square. */
export function strokeRun(from: StrokePoint, to: StrokePoint): StrokeRun {
  const runX = to[0] - from[0];
  const runY = to[1] - from[1];
  const turn = (Math.atan2(runY, runX) + Math.PI) / (Math.PI * 2);

  return {
    direction: turnStep(turn, shapeSteps),
    length: step(Math.min(1, Math.hypot(runX, runY)), shapeSteps),
    place:
      step((from[0] + to[0]) / 2, placeSteps) * placeSteps +
      step((from[1] + to[1]) / 2, placeSteps),
  };
}
